import { dispositionKindText, formatSpan, type Term } from "@/lib/campaign";
import type { DispositionInput, DispositionUpdateInput } from "@/lib/queries";
import type { Disposition, Role, Tenant } from "@/lib/types";

/**
 * The dispositions catalog as the console shows and edits it (ADR-017): what an outcome does to the
 * lead, in words, and the arithmetic between the catalog's form and `POST`/`PUT /dispositions`.
 *
 * The vocabularies mirror `DISPOSITION_KINDS` and `DISPOSITION_CATEGORIES` in `platform`'s
 * `models/dispositions.py`, and the bounds `DispositionCreate` in core-api's `schemas.py`. What
 * happens to the lead is `after_disposition` in the dialer's `recycling.py`, the one place a kind, a
 * backoff and a hold mean anything. A kind's label is `dispositionKindText` in `lib/campaign.ts`,
 * which a campaign's Dispositions tab already shows. The server decides all of it; the copies here
 * say what it will do before a save does. Pure, for `test/dispositions.test.ts`.
 */

/** `_CAMPAIGN_SLUG_RE`: what `disposition.set`, the CRM's tags and the reports carry. */
export const DISPOSITION_SLUG_RE = /^[a-z0-9][a-z0-9_-]{0,62}$/;
export const LABEL_MAX = 80;

export const DISPOSITION_KINDS = ["terminal", "callback", "retry", "hold"] as const;
export const DISPOSITION_CATEGORIES = ["contact", "no_contact", "success", "compliance"] as const;

const KIND: Record<string, string> = {
  terminal: "The lead is finished: the dialer never calls it again from this campaign.",
  callback:
    "The dialer calls the lead back after the backoff, for the rep who took this call, without spending an attempt.",
  retry:
    "One of the campaign's attempts is spent, and the dialer tries again after the backoff until they run out.",
  hold:
    "The lead is kept out of the lists until the hold has passed, as while an appointment or a contract review is pending.",
};

/** A kind in plain words. */
export function kindTerm(kind: string): Term {
  return {
    label: dispositionKindText(kind),
    description: KIND[kind] ?? "Not a kind this console knows. The dialer treats it as a retry.",
  };
}

const CATEGORY: Record<string, Term> = {
  contact: { label: "Contact", description: "The lead was reached." },
  no_contact: { label: "No contact", description: "The lead was not reached." },
  success: { label: "Success", description: "A win: signed, or an appointment set." },
  compliance: {
    label: "Compliance",
    description: "Marks the lead do-not-call, whatever the kind says.",
  },
};

/** A category's words, or null for none. One this console does not know is shown as itself. */
export function categoryTerm(category: string | null): Term | null {
  if (!category) return null;
  return CATEGORY[category] ?? { label: category, description: "Not a category this console knows." };
}

/**
 * Whether choosing it ends the lead as do-not-call rather than as its kind says. The dialer checks
 * this before the kind (`after_disposition`): the slug `dnc`, or anything in the compliance category.
 * A compliance outcome on a call with a lead also marks the lead itself do-not-call, beyond this
 * campaign (`POST /acd/calls/{id}/disposition` in core-api).
 */
export function marksDoNotCall(d: Pick<Disposition, "slug" | "category">): boolean {
  return d.slug === "dnc" || d.category === "compliance";
}

// --- when the lead is called next --------------------------------------------------------------

/** `DEFAULT_CALLBACK_SECONDS` and `DEFAULT_HOLD_SECONDS` in `recycling.py`. */
export const DEFAULT_CALLBACK_SECONDS = 3_600;
export const DEFAULT_HOLD_SECONDS = 86_400;

export interface NextCall {
  text: string;
  /** No span of the disposition's own decides it: a default does. */
  fallback: boolean;
}

/**
 * When the dialer calls the lead again, read the way `after_disposition` reads it: never after a
 * terminal or do-not-call outcome; after a callback's backoff, or an hour; after a hold's hold, else
 * its backoff, else a day; after a retry's backoff, or the cadence's next wait (ADR-067). The dialer
 * reads each span as `seconds or default`, so a zero is unset here too.
 */
export function nextCall(
  d: Pick<Disposition, "slug" | "kind" | "category" | "backoff_seconds" | "hold_seconds">,
): NextCall {
  if (marksDoNotCall(d) || d.kind === "terminal") return { text: "Never", fallback: false };
  if (d.kind === "callback") {
    return d.backoff_seconds
      ? { text: `After ${formatSpan(d.backoff_seconds)}`, fallback: false }
      : { text: `After ${formatSpan(DEFAULT_CALLBACK_SECONDS)} (default)`, fallback: true };
  }
  if (d.kind === "hold") {
    const own = d.hold_seconds || d.backoff_seconds;
    return own
      ? { text: `After ${formatSpan(own)}`, fallback: false }
      : { text: `After ${formatSpan(DEFAULT_HOLD_SECONDS)} (default)`, fallback: true };
  }
  return d.backoff_seconds
    ? { text: `After ${formatSpan(d.backoff_seconds)}`, fallback: false }
    : { text: "After the cadence's next wait", fallback: true };
}

/** The span a kind waits on: a callback and a retry their backoff, a hold its hold, a terminal
 *  outcome none. Any other kind is a retry to the dialer. A hold falls back on its backoff, which
 *  the form does not offer: one span per kind is what a person setting it up means. */
export function spanField(kind: string): "backoff" | "hold" | null {
  if (kind === "terminal") return null;
  return kind === "hold" ? "hold" : "backoff";
}

/** What an empty span field leaves the wait to, for the hint under it. */
export function spanDefaultText(kind: string, backoffSeconds: number | null): string {
  if (kind === "callback") return `Empty calls back after ${formatSpan(DEFAULT_CALLBACK_SECONDS)}.`;
  if (kind === "hold") {
    return backoffSeconds
      ? `Empty holds for its backoff, ${formatSpan(backoffSeconds)}.`
      : `Empty holds for ${formatSpan(DEFAULT_HOLD_SECONDS)}.`;
  }
  return "Empty waits the cadence's next wait.";
}

// --- the form ----------------------------------------------------------------------------------

/**
 * Whole minutes, from one to a year. The server takes any number of seconds from 0, but a zero is
 * unset to the dialer — an empty field says that — and the column is a 32-bit count of seconds,
 * which no sensible wait needs past a year.
 */
export const SPAN_MINUTES = [1, 365 * 24 * 60] as const;

/** Why a span field cannot be saved, or null when it can. Empty is the default, and fine. */
export function minutesProblem(text: string): string | null {
  const t = text.trim();
  if (t === "") return null;
  if (!/^\d+$/.test(t)) return "Whole minutes, or empty for the default";
  const minutes = Number(t);
  if (minutes < SPAN_MINUTES[0]) return "At least 1 minute, or empty for the default";
  if (minutes > SPAN_MINUTES[1]) return `At most ${SPAN_MINUTES[1].toLocaleString()} minutes, a year`;
  return null;
}

/** A span field as seconds, or null when it is empty. Only for a field `minutesProblem` passes. */
export function minutesToSeconds(text: string): number | null {
  const t = text.trim();
  return t === "" ? null : Number(t) * 60;
}

/** The catalog's form. A span is whole minutes as typed, `""` for its default; no category is `""`. */
export interface DispositionValues {
  slug: string;
  label: string;
  kind: string;
  category: string;
  backoff_minutes: string;
  hold_minutes: string;
  /** The campaign this outcome hands the lead to; "" for none (ADR-056). */
  enlist_campaign_id: string;
  requires_note: boolean;
  /** The stage the outcome puts the lead in (ADR-046); `""` keeps it. */
  stage: string;
}

export const NEW_DISPOSITION: DispositionValues = {
  slug: "",
  label: "",
  kind: "retry",
  category: "",
  backoff_minutes: "",
  hold_minutes: "",
  enlist_campaign_id: "",
  requires_note: false,
  stage: "",
};

export function dispositionValues(d: Disposition): DispositionValues {
  return {
    slug: d.slug,
    label: d.label,
    kind: d.kind,
    category: d.category ?? "",
    backoff_minutes: minutesText(d.backoff_seconds),
    hold_minutes: minutesText(d.hold_seconds),
    enlist_campaign_id: d.enlist_campaign_id ?? "",
    requires_note: d.requires_note,
    stage: d.stage ?? "",
  };
}

/** Seconds as the form's minutes, rounded — `dispositionChanges` keeps the rounding off the wire —
 *  and `""` for unset, which a zero is to the dialer. */
function minutesText(seconds: number | null): string {
  return seconds ? String(Math.max(1, Math.round(seconds / 60))) : "";
}

/** What `POST /dispositions` takes: trimmed, an empty category or span as null, only the span the
 *  kind waits on, and the place in the list it goes. */
export function newDispositionPayload(
  values: DispositionValues,
  sortOrder: number,
): Required<Omit<DispositionInput, "tenant_id">> {
  const span = spanField(values.kind);
  return {
    slug: values.slug.trim(),
    label: values.label.trim(),
    kind: values.kind,
    category: values.category || null,
    backoff_seconds: span === "backoff" ? minutesToSeconds(values.backoff_minutes) : null,
    hold_seconds: span === "hold" ? minutesToSeconds(values.hold_minutes) : null,
    enlist_campaign_id: values.enlist_campaign_id || null,
    requires_note: values.requires_note,
    sort_order: sortOrder,
    stage: values.stage || null,
  };
}

/**
 * The fields an edit changed, as `PUT /dispositions/{id}` takes them, and nothing else. A span the
 * form shows rounded to whole minutes goes out only when somebody changed it — 90 s shows as 2
 * minutes, and must not come back as 120 s because the label was fixed — and a span the kind does
 * not wait on is left as it is, not cleared behind the editor's back.
 */
export function dispositionChanges(values: DispositionValues, d: Disposition): DispositionUpdateInput {
  const before = dispositionValues(d);
  const span = spanField(values.kind);
  const out: DispositionUpdateInput = {};
  const label = values.label.trim();
  if (label !== d.label) out.label = label;
  if (values.kind !== d.kind) out.kind = values.kind;
  if (values.category !== before.category) out.category = values.category || null;
  if (span === "backoff" && values.backoff_minutes.trim() !== before.backoff_minutes) {
    out.backoff_seconds = minutesToSeconds(values.backoff_minutes);
  }
  if (span === "hold" && values.hold_minutes.trim() !== before.hold_minutes) {
    out.hold_seconds = minutesToSeconds(values.hold_minutes);
  }
  if (values.enlist_campaign_id !== before.enlist_campaign_id) {
    out.enlist_campaign_id = values.enlist_campaign_id || null;
  }
  if (values.requires_note !== d.requires_note) out.requires_note = values.requires_note;
  if (values.stage !== before.stage) out.stage = values.stage || null;
  return out;
}

// --- the order ---------------------------------------------------------------------------------

/** The gap the catalog is seeded with (`ensure_tenant_defaults`), and the one an order is saved in. */
export const SORT_STEP = 10;

/** Where a new disposition goes: after the last. The server's own default is 100, which in a seeded
 *  catalog is the tenth place. */
export function nextSortOrder(rows: readonly Pick<Disposition, "sort_order">[]): number {
  return rows.reduce((max, d) => Math.max(max, d.sort_order), 0) + SORT_STEP;
}

/** An order being edited, kept in step with the catalog under it: the ids still there, in the
 *  edited order, then any it has gained since, in catalog order. */
export function reconcileOrder(draft: readonly number[], rows: readonly Pick<Disposition, "id">[]): number[] {
  const present = new Set(rows.map((d) => d.id));
  const kept = draft.filter((id) => present.has(id));
  const seen = new Set(kept);
  return [...kept, ...rows.map((d) => d.id).filter((id) => !seen.has(id))];
}

/**
 * The writes that put the catalog in `ids` order: every row renumbered in tens, as the seed numbers
 * it, and a write only for a row whose number moves. The whole list is renumbered rather than two
 * rows swapped, because rows can share a `sort_order` — every one created with the server's default
 * is 100 — and the id breaks that tie, so swapping two equal numbers would change nothing.
 */
export function orderChanges(
  rows: readonly Pick<Disposition, "id" | "sort_order">[],
  ids: readonly number[],
): { id: number; sort_order: number }[] {
  const current = new Map(rows.map((d) => [d.id, d.sort_order]));
  const out: { id: number; sort_order: number }[] = [];
  ids.forEach((id, i) => {
    const sortOrder = (i + 1) * SORT_STEP;
    const was = current.get(id);
    if (was !== undefined && was !== sortOrder) out.push({ id, sort_order: sortOrder });
  });
  return out;
}

/**
 * Whose catalog the page shows. Below superadmin, the reader's own tenant, which core-api reads off
 * them whatever is asked. A superadmin names one — the tenant asked for in the address, else the
 * first — and with no tenant at all there is no catalog to show.
 */
export function catalogTenant(
  actor: { role: Role; tenant_id: number | null } | null,
  tenants: readonly Pick<Tenant, "id">[] | undefined,
  requested: number | null,
): number | null {
  if (!actor) return null;
  if (actor.role !== "superadmin") return actor.tenant_id;
  const first = tenants?.[0];
  if (!first) return null;
  return tenants.some((t) => t.id === requested) ? requested : first.id;
}
